/**
 * Process authentication tokens from URL parameters.
 * Root app passes tokens via URL (?acct1=...&token1=...&cur1=...) to bootstrap dtrader-c auth.
 * This replaces the unreliable cookie-based approach.
 */

type AccountToken = {
    loginid: string;
    token: string;
    currency: string;
};

/**
 * Parse authentication tokens from URL and store in localStorage.
 * Returns true if tokens were found and processed.
 * 
 * Expected URL format:
 * ?acct1=CR123&token1=a1-xxx&cur1=USD&acct2=CR456&token2=a1-yyy&cur2=XRP&account=USD
 */
export const processAuthTokensFromUrl = (): boolean => {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const hasToken1 = urlParams.has('token1');
        
        if (!hasToken1) {
            return false;
        }
        
        // Parse all accounts from URL
        const accounts: AccountToken[] = [];
        let index = 1;
        
        while (urlParams.has(`acct${index}`) && urlParams.has(`token${index}`)) {
            const loginid = urlParams.get(`acct${index}`);
            const token = urlParams.get(`token${index}`);
            const currency = urlParams.get(`cur${index}`) || 'USD';
            
            if (loginid && token) {
                accounts.push({ loginid, token, currency });
            }
            
            index++;
        }
        
        if (accounts.length === 0) {
            return false;
        }
        
        // Build accountsList and clientAccounts structures
        const accountsList: Record<string, string> = {};
        const clientAccounts: Record<string, { loginid: string; token: string; currency: string }> = {};
        
        accounts.forEach(acc => {
            accountsList[acc.loginid] = acc.token;
            clientAccounts[acc.loginid] = {
                loginid: acc.loginid,
                token: acc.token,
                currency: acc.currency,
            };
        });
        
        // Store in localStorage
        localStorage.setItem('accountsList', JSON.stringify(accountsList));
        localStorage.setItem('clientAccounts', JSON.stringify(clientAccounts));
        localStorage.setItem('client.accounts', JSON.stringify(clientAccounts));
        
        // Remove tokens from URL for security (prevent exposure in browser history)
        index = 1;
        while (urlParams.has(`acct${index}`)) {
            urlParams.delete(`acct${index}`);
            urlParams.delete(`token${index}`);
            urlParams.delete(`cur${index}`);
            index++;
        }
        
        // Update URL without tokens
        const newUrl = `${window.location.pathname}?${urlParams.toString()}${window.location.hash}`;
        window.history.replaceState({}, '', newUrl);
        
        return true;
    } catch (error) {
        console.error('[dtrader-c][processAuthTokensFromUrl] Error processing tokens:', error);
        return false;
    }
};

/**
 * Check for logout query parameter.
 * Root app sets ?logout=true when user logs out.
 * This only consumes query params; actual logout is executed by dtrader-c client logout flow.
 */
export const checkLogoutParam = (): boolean => {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const shouldLogout = urlParams.get('logout') === 'true';
        
        if (shouldLogout) {
            // Remove logout control params from URL
            urlParams.delete('logout');
            urlParams.delete('logout_ts');
            const newUrl = `${window.location.pathname}?${urlParams.toString()}${window.location.hash}`;
            window.history.replaceState({}, '', newUrl);
            
            return true;
        }
        
        return false;
    } catch (error) {
        console.error('[dtrader-c][checkLogoutParam] Error checking logout param:', error);
        return false;
    }
};
